import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Card,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  MenuItem,
  TableContainer,
  Paper,
  InputAdornment, // Import InputAdornment for the search icon
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search'; // Import SearchIcon
import axios from "axios";
import UserCabangGroups from "./UserCabangGroups";
import { apiHost } from '../utils/apiHost';
import { notif, konfirmasi } from '../utils/dialog';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const baseUrl = apiHost().replace(/^https?:\/\/+/, "");
  return `${protocol}://${baseUrl}`;
};

const fetcher = (url) => axios.get(url).then((res) => res.data.data);

function UserList() {
  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.role === "superadmin";
  const endpoint = isSuperAdmin ? "/getuser" : "/getusercabang";
  const userListKey = `${getApiBaseUrl()}${endpoint}`;
  const adminCabanguuid = user?.cabang?.uuid || user?.cabanguuid || "";
  const { data: users, error: userError } = useSWR(user ? userListKey : null, fetcher);
  //const { data: cabangList, error: cabangError } = useSWR(`${getApiBaseUrl()}/cabang`, fetcher);
  const { data: cabangList, error: cabangError } = useSWR(
    `${getApiBaseUrl()}/cabang`,
    (url) => axios.get(url).then((res) => {
      const result = res.data?.data ?? res.data;
      return Array.isArray(result) ? result : [];
    })
  );
  const safeCabangList = Array.isArray(cabangList) ? cabangList : [];
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    uuid: "",
    username: "",
    nama: "",
    password: "",
    confpassword: "",
    role: "",
    cabanguuid: "",
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const maxSize = 5 * 1024 * 1024; // 5 MB

  // State for search and filter
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("");

  const handleOpenModal = (user = null, prefill = null) => {
    if (user) {
      setIsEditing(true);
      setCurrentUser({ ...user, password: "", confpassword: "" });
    } else {
      setIsEditing(false);
      setCurrentUser({
        username: "",
        nama: "",
        password: "",
        confpassword: "",
        role: "",
        cabanguuid: prefill?.cabanguuid ?? (isSuperAdmin ? "" : adminCabanguuid),
      });
      if (prefill?.role) setCurrentUser((prev) => ({ ...prev, role: prefill.role }));
    }
    setSelectedFile(null);
    setImagePreview(null);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setCurrentUser({ username: "", nama: "", password: "", confpassword: "", role: "", cabanguuid: "" });
    setSelectedFile(null);
    setImagePreview(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setCurrentUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];

    if (!allowedTypes.includes(file.type)) {
      notif.error("Hanya file JPG, JPEG, dan PNG yang diperbolehkan.");
      return;
    }

    if (file.size > maxSize) {
      notif.error("Ukuran file maksimal 5 MB.");
      return;
    }

    setSelectedFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSaveUser = async () => {
    const newPassword = (currentUser.password || "").trim();
    const newConfPassword = (currentUser.confpassword || "").trim();

    if (!isEditing && !newPassword) {
      notif.error("Password wajib diisi untuk user baru.");
      return;
    }
    if (newPassword && newPassword !== newConfPassword) {
      notif.error("Password dan Confirm Password tidak cocok.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("username", currentUser.username);
      formData.append("nama", currentUser.nama || "");
      if (newPassword) {
        formData.append("password", newPassword);
        formData.append("confpassword", newConfPassword);
      }
      formData.append("role", currentUser.role);
      formData.append("cabanguuid", currentUser.cabanguuid);

      // Karyawan baru wajib foto; saat mengubah, foto lama dipakai bila tidak upload baru
      // (server tetap menolak bila karyawan itu belum punya foto sama sekali).
      if (currentUser.role === "karyawan" && !selectedFile && !isEditing) {
        notif.error("Foto wajib diupload untuk karyawan.");
        return;
      }
      if (selectedFile) formData.append("file", selectedFile);

      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      };

      if (isEditing) {
        await axios.put(`${getApiBaseUrl()}/updateuser/${currentUser.uuid}`, formData, config);
      } else {
        await axios.post(`${getApiBaseUrl()}/createuser`, formData, config);
      }

      mutate(userListKey);
      handleCloseModal();
    } catch (error) {
      notif.error(error.response?.data?.message || "Gagal menyimpan data user.");
    }
  };

  const handleDeleteUser = async (uuid) => {
    if (!await konfirmasi("Yakin ingin menghapus user ini?")) return;

    try {
      await axios.delete(`${getApiBaseUrl()}/deleteuser/${uuid}`, { withCredentials: true });
      mutate(userListKey);
    } catch (error) {
      notif.error(error.response?.data?.message || "Gagal menghapus user.");
    }
  };

  if (userError || cabangError) return <Typography>Error loading data.</Typography>;
  if (!users || !cabangList) return <Typography>Loading...</Typography>;

  // Filter and search logic
  const filteredUsers = users.filter((user) => {
    const matchesSearchTerm = user.username.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole ? user.role === filterRole : true;
    return matchesSearchTerm && matchesRole;
  });
  console.log("cabangList:", cabangList);


  return (
    <Box sx={{ p: 2, backgroundColor: "#f4f6f8", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Card sx={{ padding: 1, flex: 1, width: "100%", overflowX: 'auto' }}>
        <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Cari Username"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Filter Role"
              variant="outlined"
              size="small"
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">Semua Role</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="kasir">Kasir</MenuItem>
              <MenuItem value="karyawan">Karyawan</MenuItem>
              <MenuItem value="dapur">Dapur</MenuItem>
            </TextField>
          </Box>
          <Button variant="contained" color="primary" onClick={() => handleOpenModal()}>
            Tambah User
          </Button>
        </Box>

        {isSuperAdmin ? (
          <UserCabangGroups
            users={users}
            cabangList={safeCabangList}
            searchTerm={searchTerm}
            filterRole={filterRole}
            onEdit={(u) => handleOpenModal(u)}
            onDelete={handleDeleteUser}
            onAdd={(prefill) => handleOpenModal(null, prefill)}
          />
        ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>No</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Nama</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Cabang</TableCell>
                <TableCell>Aksi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user, index) => (
                <TableRow key={user.uuid}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.nama || "-"}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>{user.Cabang?.namacabang || "N/A"}</TableCell>
                  <TableCell>
                    <Button color="primary" onClick={() => handleOpenModal(user)} sx={{ mr: 1 }} size="small">
                      Edit
                    </Button>
                    <Button color="error" onClick={() => handleDeleteUser(user.uuid)} size="small">
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Tidak ada user ditemukan.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        )}

        {/* Modal Form */}
        <Modal open={openModal} onClose={handleCloseModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 300,
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" mb={2}>
              {isEditing ? "Edit User" : "Tambah User Baru"}
            </Typography>

            <TextField
              fullWidth
              label="Username"
              name="username"
              value={currentUser.username}
              onChange={handleFormChange}
              margin="normal"
              size="small"
            />
            <TextField
              fullWidth
              label="Nama"
              name="nama"
              value={currentUser.nama || ""}
              onChange={handleFormChange}
              margin="normal"
              size="small"
              helperText="Ditampilkan di struk & aplikasi, bukan username."
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              name="password"
              value={currentUser.password}
              onChange={handleFormChange}
              margin="normal"
              size="small"
              autoComplete="new-password"
              helperText={isEditing ? "Kosongkan jika tidak ingin mengubah password." : ""}
            />
            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              name="confpassword"
              value={currentUser.confpassword}
              onChange={handleFormChange}
              margin="normal"
              size="small"
              autoComplete="new-password"
            />
            <TextField
              fullWidth
              select
              label="Role"
              name="role"
              value={currentUser.role}
              onChange={handleFormChange}
              margin="normal"
              size="small"
            >
              <MenuItem value="">Pilih Role</MenuItem>
              {isSuperAdmin && <MenuItem value="superadmin">Superadmin</MenuItem>}
              {isSuperAdmin && <MenuItem value="admin">Admin</MenuItem>}
              <MenuItem value="kasir">Kasir</MenuItem>
              <MenuItem value="karyawan">Karyawan</MenuItem>
              <MenuItem value="dapur">Dapur</MenuItem>
            </TextField>

            {currentUser.role === "karyawan" && (
              <>
                <TextField
                  fullWidth
                  type="file"
                  margin="normal"
                  size="small"
                  inputProps={{ accept: ".jpg,.jpeg,.png" }}
                  onChange={handleFileChange}
                />
                {imagePreview && (
                  <Box mt={1}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: "100%",
                        maxHeight: 150,
                        objectFit: "contain",
                        border: "1px solid #ccc",
                        borderRadius: 4,
                      }}
                    />
                  </Box>
                )}
              </>
            )}

            {isSuperAdmin ? (
              <TextField
                fullWidth
                select
                label="Cabang"
                name="cabanguuid"
                value={currentUser.cabanguuid}
                onChange={handleFormChange}
                margin="normal"
                size="small"
              >
                <MenuItem value="">None</MenuItem>
                {safeCabangList.map((cabang) => (
                  <MenuItem key={cabang.uuid || 'N/A'} value={cabang.uuid || 'N/A'}>
                    {cabang.namacabang || 'N/A'}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                fullWidth
                label="Cabang"
                value={safeCabangList.find((c) => c.uuid === adminCabanguuid)?.namacabang || 'Cabang Anda'}
                margin="normal"
                size="small"
                disabled
                helperText="User baru otomatis dibuat di cabang Anda."
              />
            )}

            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button onClick={handleCloseModal} sx={{ mr: 1 }} size="small">
                Batal
              </Button>
              <Button variant="contained" color="primary" onClick={handleSaveUser} size="small">
                {isEditing ? "Simpan" : "Tambah"}
              </Button>
            </Box>
          </Box>
        </Modal>
      </Card>
    </Box>
  );
}

export default UserList;